/**
 * Phase power plan — provenance stamp, ordered staleness, and the Apply
 * projection. Mirrors the max-power stamp doctrine (maxPowerIntentStamp.ts):
 * value + provenance written in ONE projection; hash-based staleness with an
 * ordered, actionable FIRST reason; a stale stamp describes history, never
 * authority (manual edits win).
 *
 * The stamp lives on the INTENT (`intent.phasePowerPlan`) because the plan is
 * campus-coherent — phases are interdependent through cumulative floors. The
 * applied VALUES live in ordinary `componentParams[k].capacityMW` (with the
 * per-entry `capacitySource: 'phase-power-plan'` marker) so every downstream
 * consumer keeps working unchanged.
 *
 * HASH DISCIPLINE: `hashGenerationImpactPayload` (the same stable digest the
 * max-power stamp uses). The availability hash covers the RESOLVER OUTPUT,
 * not the raw SED — unrelated SED churn must not dirty the plan.
 */
import type {
  PhasePowerComponentKey,
  ProjectIntent,
  ProjectPhase,
} from '../../types/siteRag';
import { hashGenerationImpactPayload } from '../generationImpact/integrity';
import { withPhasePowerSourceConfig, withPhaseSourceCapacityMW } from '../projectIntent';
import type { PerSourceAvailabilityResolution } from './resolvePerSourceAvailability';
import type { PhasePowerPlanResolution } from './recommendPhasePowerPlan';

/** Bump on ANY engine algorithm change — trips staleness fleet-wide (intended). */
export const PHASE_POWER_PLAN_MODEL_VERSION = 'phase-power-plan-v1';

export interface PhasePowerPlanHashInputs {
  intent: ProjectIntent;
  availability: PerSourceAvailabilityResolution;
  /** The envelope identity — the stamp's studyHash, or 'none'. */
  envelopeHash: string;
}

export interface PhasePowerPlanSubHashes {
  intentSlice: string;
  availability: string;
  envelope: string;
}

/**
 * The canonical intent slice the plan depends on — per-phase ranges, mixes
 * and the campus goal. Anything OUTSIDE this slice (cooling, timeline,
 * notes…) must never dirty the plan.
 */
function intentSliceForHash(intent: ProjectIntent): unknown {
  return {
    // Hash-slice READ of the canonical projectIntent field (the campus goal),
    // not a per-prefs shadow — the SSOT sentinel bans the bare identifier.
    powerRequirementMW: intent.powerRequirementMW ?? null, // SSOT-SENTINEL-OK — canonical projectIntent field in the staleness hash slice
    powerRequirement: intent.powerRequirement ?? null,
    phases: (intent.phases ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map(p => ({
        id: p.id,
        sequence: p.sequence,
        capacityMW: p.capacityMW ?? null,
        capacityBucket: p.capacityBucket ?? null,
        components: p.powerSourceConfig?.components ?? [],
        mixMode: p.powerSourceConfig?.mixMode ?? null,
        componentParams: p.powerSourceConfig?.componentParams ?? {},
      })),
  };
}

function availabilitySliceForHash(availability: PerSourceAvailabilityResolution): unknown {
  return {
    perSource: Object.fromEntries(
      Object.entries(availability.perSource).map(([k, v]) => [
        k,
        { status: v.status, availableMW: v.availableMW, basis: v.basis },
      ]),
    ),
    sharedConstraints: availability.sharedConstraints.map(c => ({
      id: c.id,
      capMW: c.capMW,
      capDthPerDay: c.capDthPerDay,
      basis: c.basis,
    })),
  };
}

export function buildPhasePowerPlanSubHashes(
  inputs: PhasePowerPlanHashInputs,
): PhasePowerPlanSubHashes {
  return {
    intentSlice: hashGenerationImpactPayload(intentSliceForHash(inputs.intent)),
    availability: hashGenerationImpactPayload(availabilitySliceForHash(inputs.availability)),
    envelope: inputs.envelopeHash,
  };
}

export interface PhasePowerPlanStalenessArgs {
  intent: ProjectIntent | null | undefined;
  /** Sub-hashes recomputed from the CURRENT inputs. */
  currentSubHashes: PhasePowerPlanSubHashes;
}

/**
 * The FIRST reason the applied plan no longer describes reality, or null when
 * fresh. Ordered — model → phase set → declared ranges/mixes → availability →
 * envelope → manual edit — so the user always sees the most upstream cause.
 */
export function phasePowerPlanStalenessReason(
  args: PhasePowerPlanStalenessArgs,
): string | null {
  const intent = args.intent;
  const stamp = intent?.phasePowerPlan;
  if (!intent || !stamp) return null; // nothing applied — nothing to be stale

  if (stamp.planVersion !== PHASE_POWER_PLAN_MODEL_VERSION) {
    return 'The recommendation model changed since this plan was applied — re-run the recommendation.';
  }
  const phasesById = new Map((intent.phases ?? []).map(p => [p.id, p]));
  for (const applied of stamp.appliedPhases) {
    if (!phasesById.has(applied.phaseId)) {
      return 'The phase set changed since the plan was applied (an applied phase was removed or re-created) — re-run the recommendation.';
    }
  }
  if (stamp.subHashes.intentSlice !== args.currentSubHashes.intentSlice) {
    return 'Phase ranges or source mixes changed since the plan was applied — re-run the recommendation.';
  }
  if (stamp.subHashes.availability !== args.currentSubHashes.availability) {
    return 'Grid/gas availability evidence changed since the plan was applied — re-run the recommendation.';
  }
  if (stamp.subHashes.envelope !== args.currentSubHashes.envelope) {
    return 'The max-power envelope changed since the plan was applied — re-run the recommendation.';
  }
  for (const applied of stamp.appliedPhases) {
    const phase = phasesById.get(applied.phaseId)!;
    for (const src of applied.perSource) {
      const stored = phase.powerSourceConfig?.componentParams?.[src.key];
      if (stored?.capacitySource !== 'phase-power-plan' || stored?.capacityMW !== src.mw) {
        return 'The applied figures were manually edited since the plan was applied — the manual values win; re-run to refresh the plan.';
      }
    }
  }
  return null;
}

export interface ApplyPhasePowerPlanArgs {
  intent: ProjectIntent;
  plan: Extract<PhasePowerPlanResolution, { status: 'available' }>;
  /** Which phases to apply (must be 'recommended' entries). */
  phaseIds: readonly string[];
  subHashes: PhasePowerPlanSubHashes;
  now: string;
  uid?: string;
}

/**
 * PURE — project the plan's recommended per-source figures onto the intent
 * through the EXISTING writers (no new write path):
 *   - per-source exact MW via `withPhaseSourceCapacityMW(..., 'phase-power-plan')`
 *     (drops range edges — exact-wins made physical — and stamps the marker);
 *   - a CF is written only when the user declared none (the engine's default
 *     is a planning figure, not a user statement);
 *   - `phase.capacityMW` is NEVER written — in range mode the user's declared
 *     range stays authoritative (the recommended load lives in the stamp and
 *     UI), and writing it would fight reconcilePowerRequirementWithPhases.
 * Phases are replaced IN PLACE by id (array order is load-bearing for
 * hashProjectIntent).
 */
export function applyPhasePowerPlanToIntent(args: ApplyPhasePowerPlanArgs): ProjectIntent {
  const { intent, plan, phaseIds, subHashes, now, uid } = args;
  const wanted = new Set(phaseIds);
  const entries = plan.perPhase.filter(
    e => wanted.has(e.phaseId) && e.status === 'recommended' && e.recommendedLoadMW != null,
  );
  if (entries.length === 0) {
    throw new RangeError(
      'No recommended phase entries match the requested phase ids — applying "no answer" would fabricate a plan.',
    );
  }
  const byId = new Map(entries.map(e => [e.phaseId, e]));

  const phases = (intent.phases ?? []).map(phase => {
    const entry = byId.get(phase.id);
    if (!entry) return phase;
    let next: ProjectPhase = phase;
    for (const src of entry.perSource) {
      next = withPhaseSourceCapacityMW(next, src.key, src.recommendedMW, 'phase-power-plan');
      const declaredCf = phase.powerSourceConfig?.componentParams?.[src.key]?.capacityFactorPct;
      if (declaredCf == null) {
        const cfg = next.powerSourceConfig!;
        next = withPhasePowerSourceConfig(next, {
          ...cfg,
          componentParams: {
            ...(cfg.componentParams ?? {}),
            [src.key]: {
              ...(cfg.componentParams?.[src.key] ?? {}),
              capacityFactorPct: src.recommendedCapacityFactorPct,
            },
          },
        });
      }
    }
    return next;
  });

  const nextIntent: ProjectIntent = { ...intent, phases };
  // The stamped intent slice must describe the POST-apply intent — the apply
  // itself rewrites ranges into exacts, and stamping the pre-apply slice
  // would make every fresh apply INSTANTLY stale ("ranges changed") against
  // its own write. Availability/envelope hashes are inputs the apply doesn't
  // touch and carry through unchanged. (Found by live QA, pinned by test.)
  const stampedSubHashes: PhasePowerPlanSubHashes = {
    ...subHashes,
    intentSlice: hashGenerationImpactPayload(intentSliceForHash(nextIntent)),
  };
  return {
    ...nextIntent,
    phasePowerPlan: {
      planVersion: PHASE_POWER_PLAN_MODEL_VERSION,
      inputsHash: hashGenerationImpactPayload(stampedSubHashes),
      subHashes: stampedSubHashes,
      appliedAt: now,
      ...(uid ? { appliedBy: uid } : {}),
      appliedPhases: entries.map(e => ({
        phaseId: e.phaseId,
        recommendedLoadMW: e.recommendedLoadMW!,
        perSource: e.perSource.map(s => ({ key: s.key, mw: s.recommendedMW })),
      })),
    },
  };
}

/** True when a plan has been applied to this intent (stamp present). */
export function isPhasePowerPlanApplied(intent: ProjectIntent | null | undefined): boolean {
  return intent?.phasePowerPlan != null;
}
